import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ObjectUtility } from '../../../../classes/objectUtility';
import { ICommodityType, ICommodityTypeFormResult } from '../../../../models/commodity-type/interfaces';
import { CommodityService } from '../../../../models/commodity-type/service';
import { CommodityType } from '../../../../models/commodity-type/commodityType';
import { CropType } from '../../../../models/crop-type/cropType';
import { ICropType, ICropTypeListItem } from '../../../../models/crop-type/interfaces';
import { CropTypeService } from '../../../../models/crop-type/service';
import { FormComponent } from '../../../shared/form/form';
import { SharedKeepDiscardDialog } from '../../../shared/dialogs/keep-discard-dialog';
import { SharedUpdateService } from '../../../shared/dialogs/update.service';

@Component({
	moduleId: module.id,
	selector: 'ranch-commodity-type-form',
	templateUrl: 'main.html'
})

export class RanchCommodityTypeFormComponent extends FormComponent implements OnInit, OnDestroy {
	@Input()
	public isCommodityTypeSelectionAllowed = true;

	@Input()
	public commodityTypeId: number;

	@Input()
	public commodityTypeName: string;

	@Input()
	public cropTypeIds: number[] = [];

	@Input()
	public ranchGuid: string;

	@Input()
	public selectedCommodityTypeIds: number[] = [];

	@Input()
	public isRanchAgnostic = false;

	@Output()
	public onCancel: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onHide: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onShow: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public formSubmit: EventEmitter<ICommodityTypeFormResult> = new EventEmitter<ICommodityTypeFormResult>();

	public commodityTypes: ICommodityType[];
	public cropTypes: ICropType[];
	public isFormValid = false;
	public loadingData = true;
	public selectedCommodityType: ICommodityType;

	public formErrors = {
		'commodityTypeId': '',
	};

	public validationMessages = {
		'commodityTypeId': {
			'required': 'commodity type is required.',
		},
	};

	private _cropTypesOriginal: ICropType[];
	private _originalCommodityTypeId: number;

	constructor(
		private _commodityService: CommodityService,
		private _cropTypeService: CropTypeService,
		private _dialog: MatDialog,
		private _sharedUpdateService: SharedUpdateService
	) {
		super();
	}

	public ngOnInit(): void {
		this._originalCommodityTypeId = this.commodityTypeId;
		this._setupPopupSubscriptions();

		if (this.isCommodityTypeSelectionAllowed) {
			this._loadCommodityTypes();
		}

		if (this.commodityTypeId) {
			this._loadCropTypes(this.commodityTypeId);
		} else {
			this.loadingData = false;
		}
	}

	public ngOnDestroy(): void {
		if (!this._subscriptions$) {
			return;
		}

		this._subscriptions$.next(true);
		this._subscriptions$.complete();
	}

	public cancel(): void {
		if (this._isDirty()) {
			this._dialog.open(SharedKeepDiscardDialog, {
				width: '500px',
				disableClose: true,
				autoFocus: false
			});

			this.onHide.emit();
		} else {
			this.onCancel.emit();
		}
	}

	public onCommodityTypeSelected(commodityTypeId: number): void {
		if (commodityTypeId === null) {
			this.commodityTypeId = null;
			this.selectedCommodityType = null;
			this.cropTypes = null;
			this.validateCropTypes();
			return;
		}

		if (!this.commodityTypes) {
			return;
		}

		this.commodityTypeId = commodityTypeId;
		this.selectedCommodityType = this.commodityTypes.find(x => x.Id === commodityTypeId);
		this.commodityTypeName = this.selectedCommodityType ? this.selectedCommodityType.Name : null;
		this._loadCropTypes(commodityTypeId);
	}

	public onCropTypeChange(): void {
		this.validateCropTypes();
	}

	public submit(): void {
		this.validateCropTypes();

		if (!this.isFormValid) {
			return;
		}

		this.formSubmit.emit({
			CommodityTypeId: this.commodityTypeId,
			CommodityTypeName: this._getCommodityTypeName(),
			CropTypeIds: CropType.getIdsFromArray(this.cropTypes)
		});
	}

	public validateCropTypes(cropTypeId?: number): void {
		let count: number;
		let countAssociated: number;

		count = 0;
		countAssociated = 0;

		if (!this.commodityTypeId || !this.cropTypes || this.cropTypes.length === 0) {
			this.isFormValid = false;
			return;
		}

		for (let cropType of this.cropTypes) {
			if (cropTypeId && cropType.Id === cropTypeId && cropType.IsSelected === false) {
				count++;
			} else if (cropType.IsSelected) {
				count++;
			}

			if (cropType.PlantingsCount > 0 && cropType.IsSelected === false) {
				countAssociated++;
			}
		}

		this.isFormValid = count > 0 && countAssociated === 0;
	}

	private _applySelectedCropTypes(): void {
		if (!this.cropTypes || !this.cropTypeIds) {
			return;
		}

		for (let cropType of this.cropTypes) {
			cropType.IsSelected = this.cropTypeIds.indexOf(cropType.Id) !== -1 || cropType.IsSelected;
		}
	}

	private _getCommodityTypeName(): string {
		if (this.selectedCommodityType) {
			return this.selectedCommodityType.Name;
		}

		return this.commodityTypeName;
	}

	private _isDirty(): boolean {
		if (this.commodityTypeId !== this._originalCommodityTypeId) {
			return true;
		}

		return !ObjectUtility.isEqual(this.cropTypes, this._cropTypesOriginal);
	}

	private _loadCommodityTypes(): void {
		if (this.isRanchAgnostic) {
			this._commodityService.list().then(data => {
				this.commodityTypes = data.filter(x => this.selectedCommodityTypeIds.indexOf(x.Id) === -1);
				this.selectedCommodityType = this.commodityTypes.find(x => x.Id === this.commodityTypeId);
			});
		} else {
			this._commodityService.listAllByRanch(this.ranchGuid).then(data => {
				this.commodityTypes = CommodityType.filterAvailable(data);
			});
		}
	}

	private _loadCropTypes(commodityTypeId: number): void {
		this.loadingData = true;

		if (this.isRanchAgnostic) {
			this._cropTypeService.getCropTypesAdmin(false, commodityTypeId).then(data => {
				this.cropTypes = this._mapCropTypeListItems(data);
				this._applySelectedCropTypes();
				this.validateCropTypes();
				this._cropTypesOriginal = ObjectUtility.copyArray(this.cropTypes);
				this.loadingData = false;
			});
		} else {
			this._cropTypeService.getAllByRanch(this.ranchGuid, commodityTypeId).then(data => {
				this.cropTypes = data;
				this._applySelectedCropTypes();
				this.validateCropTypes();
				this._cropTypesOriginal = ObjectUtility.copyArray(this.cropTypes);
				this.loadingData = false;
			});
		}
	}

	private _mapCropTypeListItems(cropTypes: ICropTypeListItem[]): ICropType[] {
		if (!cropTypes) {
			return [];
		}

		return cropTypes.map((cropType) => {
			return {
				Id: cropType.Id,
				Name: cropType.Name,
				UptakeConstant: 0,
				UptakeFactor: 0,
				IsSelected: false,
				PlantingsCount: 0
			};
		});
	}

	private _setupPopupSubscriptions(): void {
		this._sharedUpdateService.keep$.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.onShow.emit();
		});

		this._sharedUpdateService.discard$.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.onCancel.emit();
		});
	}
}
